// src/features/auth/services/authService.js
// Enterprise-grade authentication service - All Firebase auth operations isolated

import { auth } from '@/shared/utils/core/firebase-config';
import { logOAuthDiagnostics, getOAuthEnvironmentDetails } from '../utils/oauthHelper';
import {
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from 'firebase/auth';
import analytics from '@/shared/utils/analytics/analytics';
import logger from '@/shared/utils/core/logger';
import { isNative } from '@/shared/utils/core/platform';

// ---------------------------------------------------------------------------
// Safe storage helpers
// WKWebView with ITP can throw on localStorage / sessionStorage access.
// These wrappers swallow the error so auth flows never crash on storage ops.
// ---------------------------------------------------------------------------
const safeLocalStorage = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch { /* ignore */ } },
  removeItem: (key) => { try { localStorage.removeItem(key); } catch { /* ignore */ } },
};
const safeSessionStorage = {
  getItem: (key) => { try { return sessionStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* ignore */ } },
  removeItem: (key) => { try { sessionStorage.removeItem(key); } catch { /* ignore */ } },
};

const googleProvider = new GoogleAuthProvider();

// Keep configuration minimal - Firebase handles most settings automatically
// Only set prompt to always show account selector
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

/**
 * Initialize Google Auth - MUST be called on every app initialization
 * This handles the redirect result from Google OAuth
 *
 * @returns {Promise<{user: User|null, isNewUser: boolean, error: Error|null}>}
 */
export async function initializeGoogleAuth() {
  const LOG_PREFIX = '[GoogleAuth Init]';

  // On native (Capacitor), getRedirectResult() is a web-only mechanism.
  // It tries to communicate with the Firebase auth domain via a hidden iframe,
  // which WKWebView blocks — causing the auth listener to hang indefinitely.
  // Google OAuth on native uses the system browser and resolves via onAuthStateChanged.
  if (isNative()) {
    return { user: null, isNewUser: false, error: null };
  }

  try {
    if (import.meta.env.DEV) {
      logger.debug(`${LOG_PREFIX} Checking for redirect result...`);
    }

    // CRITICAL: Always call getRedirectResult on app init
    // Firebase stores the pending credential and will return it here
    const result = await getRedirectResult(auth);

    if (result) {
      // Success! User has returned from Google sign-in
      const isNewUser = result._tokenResponse?.isNewUser || false;

      if (import.meta.env.DEV) {
        logger.debug(`${LOG_PREFIX} Redirect sign-in successful:`, {
          email: result.user.email,
          uid: result.user.uid,
          isNewUser
        });
      }

      // Track success
      analytics.trackLoginSuccess('google', result.user.uid, true);

      // Clear any pending flags
      safeSessionStorage.removeItem('clarity_google_auth_pending');
      safeLocalStorage.removeItem('clarity_google_redirect_pending');

      return {
        user: result.user,
        isNewUser,
        error: null
      };
    }

    if (import.meta.env.DEV) {
      logger.debug(`${LOG_PREFIX} No redirect result found (normal if not returning from OAuth)`);
    }

    return { user: null, isNewUser: false, error: null };

  } catch (error) {
    logger.error(`${LOG_PREFIX} Error processing redirect result:`, {
      code: error.code,
      message: error.message
    });

    // Clear pending flags on error
    safeSessionStorage.removeItem('clarity_google_auth_pending');
    safeLocalStorage.removeItem('clarity_google_redirect_pending');

    // Track failure
    analytics.trackLoginFailure('google', error.code, error.message);

    return { user: null, isNewUser: false, error };
  }
}

/**
 * Detect the best authentication method for current environment
 * @returns {'popup' | 'redirect'}
 */
function detectAuthMethod() {
  // Native WebViews block popups — always use redirect
  if (isNative()) return 'redirect';

  // Only use redirect if we're in an iframe (popups blocked in iframes)
  const isInIframe = window.self !== window.top;
  if (isInIframe) return 'redirect';

  // Use popup for everything else - desktop, mobile, PWA
  // Popup is more reliable and doesn't have cross-site cookie issues
  // If popup fails, executePopupAuthWithFallback will fall back to redirect
  return 'popup';
}

/**
 * Sign in with Google - Enterprise implementation with automatic fallback
 *
 * @param {boolean} rememberMe - Whether to persist session across browser closes
 * @returns {Promise<UserCredential|null>} - Returns null if redirect is initiated
 */
export async function loginWithGoogle(rememberMe = true) {
  const LOG_PREFIX = '[GoogleAuth Login]';

  // Track attempt (fire and forget - don't block)
  analytics.trackLoginAttempt('google', rememberMe);

  // Native: use custom GoogleSignInPlugin (no Facebook SDK bloat).
  // The plugin is defined in ios/App/App/GoogleSignInPlugin.swift.
  if (isNative()) {
    try {
      // Use window.Capacitor.registerPlugin directly — avoids dynamic import of @capacitor/core
      // which is marked external and would throw "does not resolve to a valid URL" in WKWebView.
      const GoogleSignIn = window.Capacitor.registerPlugin('GoogleSignIn');
      const result = await GoogleSignIn.signIn();
      const credential = GoogleAuthProvider.credential(result.idToken, result.accessToken || null);
      const userCredential = await signInWithCredential(auth, credential);
      analytics.trackLoginSuccess('google', userCredential.user.uid, rememberMe);
      return userCredential;
    } catch (error) {
      // User tapped Cancel in the Google Sign-In sheet — treat as silent dismissal,
      // same as auth/popup-closed-by-user on web.
      if (error.code === 'SIGN_IN_CANCELLED') {
        return null;
      }
      analytics.trackLoginFailure('google', error.code, error.message);
      throw error;
    }
  }

  const method = detectAuthMethod();

  // Set persistence (fire and forget - don't block popup)
  // Mobile browsers require popup to open synchronously from user gesture
  setPersistence(
    auth,
    rememberMe ? browserLocalPersistence : browserSessionPersistence
  ).catch(err => logger.warn('Failed to set persistence:', err));

  try {
    if (method === 'redirect') {
      return await executeRedirectAuth(rememberMe, LOG_PREFIX);
    } else {
      return await executePopupAuthWithFallback(rememberMe, LOG_PREFIX);
    }

  } catch (error) {
    // Track failure
    analytics.trackLoginFailure('google', error.code, error.message);
    throw error;
  }
}

/**
 * Execute redirect-based authentication
 */
async function executeRedirectAuth(rememberMe, LOG_PREFIX) {
  if (import.meta.env.DEV) {
    logger.debug(`${LOG_PREFIX} Using redirect method`);
  }

  // Store state for when user returns
  safeSessionStorage.setItem('clarity_google_auth_pending', 'true');
  safeSessionStorage.setItem('clarity_google_auth_time', Date.now().toString());
  safeLocalStorage.setItem('clarity_google_redirect_pending', 'true');
  safeLocalStorage.setItem('clarity_remember_me', rememberMe ? 'true' : 'false');

  // Initiate redirect - this navigates away from the page
  await signInWithRedirect(auth, googleProvider);

  // This line is reached only if redirect fails immediately
  return null;
}

/**
 * Execute popup-based authentication with automatic redirect fallback
 */
async function executePopupAuthWithFallback(rememberMe, LOG_PREFIX) {
  if (import.meta.env.DEV) {
    logger.debug(`${LOG_PREFIX} Trying popup method`);
  }

  try {
    // Direct call to signInWithPopup - don't wrap in analytics for cleaner error handling
    const result = await signInWithPopup(auth, googleProvider);

    if (import.meta.env.DEV) {
      logger.debug(`${LOG_PREFIX} Popup sign-in successful:`, result.user.email);
    }

    // Track success
    analytics.trackLoginSuccess('google', result.user.uid, rememberMe);
    safeLocalStorage.setItem('clarity_remember_me', rememberMe ? 'true' : 'false');

    return result;

  } catch (popupError) {
    if (import.meta.env.DEV) {
      logger.error(`${LOG_PREFIX} Popup error:`, {
        code: popupError.code,
        message: popupError.message,
        fullError: popupError
      });
    }

    // Handle popup-specific errors by falling back to redirect
    const fallbackCodes = [
      'auth/popup-blocked',
      'auth/popup-closed-by-user',
      'auth/cancelled-popup-request'
    ];

    if (fallbackCodes.includes(popupError.code)) {
      if (import.meta.env.DEV) {
        logger.debug(`${LOG_PREFIX} Popup failed (${popupError.code}), falling back to redirect`);
      }

      return await executeRedirectAuth(rememberMe, LOG_PREFIX);
    }

    // For internal-error, log detailed diagnostics using OAuth helper
    if (popupError.code === 'auth/internal-error') {
      // Use the OAuth helper for comprehensive diagnostics
      logOAuthDiagnostics();

      const env = getOAuthEnvironmentDetails();

      logger.error(`${LOG_PREFIX} === INTERNAL ERROR DIAGNOSTICS ===`);
      logger.error(`${LOG_PREFIX} Auth Domain from config: ${auth.config?.authDomain || 'N/A'}`);
      logger.error(`${LOG_PREFIX} API Key present: ${!!auth.config?.apiKey}`);
      logger.error(`${LOG_PREFIX} Full error:`, JSON.stringify(popupError, null, 2));
      logger.error(`${LOG_PREFIX} Error customData:`, popupError.customData);
      logger.error(`${LOG_PREFIX} ================================`);
      logger.error(`${LOG_PREFIX} See docs/GOOGLE_OAUTH_SETUP.md for detailed setup instructions`);
      logger.error(`${LOG_PREFIX} ================================`);

      const enhancedError = new Error(
        `Google Sign-In configuration required. Add "${env.origin}" to OAuth settings. Check console for setup instructions.`
      );
      enhancedError.code = popupError.code;
      enhancedError.originalError = popupError;
      enhancedError.oauthEnv = env;
      throw enhancedError;
    }

    // Re-throw errors that shouldn't trigger fallback
    throw popupError;
  }
}

/**
 * Check if there's a pending Google auth that we're waiting for
 * @returns {boolean}
 */
export function isGoogleAuthPending() {
  const pendingSession = safeSessionStorage.getItem('clarity_google_auth_pending') === 'true';
  const pendingLocal = safeLocalStorage.getItem('clarity_google_redirect_pending') === 'true';

  // Check if the pending auth is stale (older than 5 minutes)
  const authTime = safeSessionStorage.getItem('clarity_google_auth_time');
  if (authTime) {
    const elapsed = Date.now() - parseInt(authTime, 10);
    if (elapsed > 5 * 60 * 1000) {
      // Stale - clear it
      safeSessionStorage.removeItem('clarity_google_auth_pending');
      safeSessionStorage.removeItem('clarity_google_auth_time');
      safeLocalStorage.removeItem('clarity_google_redirect_pending');
      return false;
    }
  }

  return pendingSession || pendingLocal;
}

/**
 * Clear any pending Google auth state
 */
export function clearGoogleAuthPending() {
  safeSessionStorage.removeItem('clarity_google_auth_pending');
  safeSessionStorage.removeItem('clarity_google_auth_time');
  safeLocalStorage.removeItem('clarity_google_redirect_pending');
}

/**
 * Sign in with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {boolean} rememberMe - Whether to persist session
 * @returns {Promise<UserCredential>} - Firebase user credential
 */
export async function loginWithEmail(email, password, rememberMe = true) {
  try {
    // Track attempt
    analytics.trackLoginAttempt('email', rememberMe);

    // Set persistence
    await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);

    // Measure performance
    const result = await analytics.measureAuthPerformance('email_login', async () => {
      return await signInWithEmailAndPassword(auth, email, password);
    });

    // Track success
    analytics.trackLoginSuccess('email', result.user.uid, rememberMe);

    // Save preference
    safeLocalStorage.setItem('clarity_remember_me', rememberMe ? 'true' : 'false');

    return result;
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Email login error:', error);
    }

    // Track failure
    analytics.trackLoginFailure('email', error.code, error.message);

    throw error;
  }
}

/**
 * Sign up with email and password
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} displayName - User display name
 * @returns {Promise<UserCredential>} - Firebase user credential
 */
export async function signupWithEmail(email, password, displayName) {
  try {
    // Track attempt
    analytics.trackSignupAttempt('email');

    // Set persistence to local by default for new users
    await setPersistence(auth, browserLocalPersistence);

    // Measure performance
    const result = await analytics.measureAuthPerformance('email_signup', async () => {
      return await createUserWithEmailAndPassword(auth, email, password);
    });

    // Update profile with display name
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
    }

    // Send email verification — continueUrl always uses the production domain so
    // the link works from both web and native (Capacitor's window.location.origin
    // resolves to capacitor://localhost which Firebase cannot redirect to).
    // On iOS with Universal Links configured, the clarityjournal.co redirect
    // opens the native app directly instead of staying in Safari.
    if (result.user && !result.user.emailVerified) {
      try {
        const actionCodeSettings = {
          url: 'https://clarityjournal.co/profile?verified=true',
          handleCodeInApp: false
        };
        await sendEmailVerification(result.user, actionCodeSettings);
      } catch (emailErr) {
        // Account was created successfully — don't fail signup just because
        // the verification email couldn't be sent (e.g. rate limit, network)
        logger.warn('[AuthService] Failed to send verification email (account created OK):', emailErr);
      }
    }

    // Track success
    analytics.trackSignupSuccess('email', result.user.uid);

    // Save preference
    safeLocalStorage.setItem('clarity_remember_me', 'true');

    return result;
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Email signup error:', error);
    }

    // Track failure
    analytics.trackSignupFailure('email', error.code, error.message);

    throw error;
  }
}

/**
 * Send password reset email
 * @param {string} email - User email
 * @returns {Promise<void>}
 */
export async function resetPassword(email) {
  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/login`,
      handleCodeInApp: true // Use our custom handler at /auth/action
    };
    await sendPasswordResetEmail(auth, email, actionCodeSettings);

    // Track success
    analytics.trackPasswordResetRequest(true);
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Password reset error:', error);
    }

    // Track failure
    analytics.trackPasswordResetRequest(false, error.message);

    throw error;
  }
}

/**
 * Sign out current user
 * @param {string} userId - User ID for analytics (optional)
 * @returns {Promise<void>}
 */
export async function logout(userId) {
  try {
    await firebaseSignOut(auth);

    // Clear any auth-related storage
    clearGoogleAuthPending();

    // Track logout
    if (userId) {
      analytics.trackLogout(userId);
    }

    // End session
    analytics.endSession();
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Logout error:', error);
    }
    throw error;
  }
}

/**
 * Update user profile
 * @param {Object} updates - Profile updates (displayName, photoURL, etc.)
 * @returns {Promise<void>}
 */
export async function updateUserProfile(updates) {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    await updateProfile(user, updates);
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Profile update error:', error);
    }
    throw error;
  }
}

/**
 * Resend email verification
 * @returns {Promise<void>}
 */
export async function resendEmailVerification() {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    if (user.emailVerified) {
      throw new Error('Email is already verified');
    }

    const actionCodeSettings = {
      url: 'https://clarityjournal.co/profile?verified=true',
      handleCodeInApp: false
    };
    await sendEmailVerification(user, actionCodeSettings);
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Email verification error:', error);
    }
    throw error;
  }
}

/**
 * Set authentication persistence
 * @param {boolean} rememberMe - Whether to persist session
 * @returns {Promise<void>}
 */
export async function setAuthPersistence(rememberMe) {
  try {
    await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
    safeLocalStorage.setItem('clarity_remember_me', rememberMe ? 'true' : 'false');
  } catch (error) {
    if (import.meta.env.DEV) {
      logger.error('Set persistence error:', error);
    }
    throw error;
  }
}

/**
 * Get current auth instance
 * @returns {object} - Firebase auth instance
 */
export function getAuthInstance() {
  return auth;
}
